export interface TimestampMixin {
  dateCreated?: number;
  dateUpdated?: number;
}

export enum AIChatBotLLMType {
  gpt_3_5_turbo = 'gpt_3_5_turbo',
  gpt_4o = 'gpt_4o',
}

export enum AIChatBotDataSourceType {
  file = 'file',
  website_link = 'website_link',
  simple_text = 'simple_text',
  question_answer = 'question_answer',
}

export interface AIChatBotCreate {
  name: string;
  modelType: AIChatBotLLMType;
  modelTemperature: number;
  instructions?: string;
}

export enum AiChatbotJobType {
  SCRAPE = 'scrape',
  GENERATE_FAQ = 'generate_faq',
}
export type AiChatbotJobStatus = 'in_progress' | 'completed' | 'error';

export enum DataSourceOrigin {
  AI = 'AI',
  SYSTEM = 'SYSTEM',
  USER = 'USER',
}

export enum FaqAnswerStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export interface AiChatBotJob {
  id: number;
  jobType: AiChatbotJobType;
  status: AiChatbotJobStatus;
  dateCreated: number;
  dateUpdated: number;
}

export interface AIChatBot extends TimestampMixin {
  id: number;
  modelType: string;
  enabled: boolean;
  cobrowseEnabled: boolean;
  recordingEnabled: boolean;
  conversationStartersEnabled: boolean;
  modelTemperature: number;
  instructions?: string;
  openaiAssistantId?: string;
  jobs: AiChatBotJob[];
  customSchedulingLink?: string | null;
  calendarEventTypeId?: number | null;
  schedulingPriority?: SchedulingPriority | null;
  defaultRep?: number | null;
  refreshConfig?: AIChatBotRefreshConfig | null;
  excludedPaths?: string[] | null;
}

export interface AIChatBotDataSource extends TimestampMixin {
  id: number | string;
  aiChatbotId: number;
  sourceType: AIChatBotDataSourceType;
  isUsedForTraining?: boolean;
  isAiGenerated?: boolean;
  charactersCount?: number;
}

export interface FileDataSource extends AIChatBotDataSource {
  s3Key: string;
}

export interface WebsiteDataSource extends AIChatBotDataSource {
  url: string;
  s3Key?: string;
}

export interface TextDataSource extends AIChatBotDataSource {
  body: string;
}

export interface FaqAnswer extends TimestampMixin {
  id: number;
  answer: string;
  status: FaqAnswerStatus;
  origin: DataSourceOrigin;
  sources: FaqSource[];
}

export interface QADataSource extends TimestampMixin {
  origin: DataSourceOrigin;
  question: string;
  answer: FaqAnswer | null;
  proposedAnswer: FaqAnswer | null;
  id?: number;
}

export interface FaqSource {
  url: string;
  relevancyScore: number;
}

export type PolymorphicAIChatBotDataSource =
  | TextDataSource
  | QADataSource
  | WebsiteDataSource
  | FileDataSource;

export type PatchQADataSource = {
  question?: string;
};

export enum SchedulingPriority {
  CUSTOM_LINK = 'CUSTOM_LINK',
  CALENDAR_EVENT = 'CALENDAR_EVENT',
}

export interface ConversationStarter {
  id: number;
  aiChatbotId: number;
  suggestion: string;
}

export enum AIChatBotRefreshFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

export interface AIChatBotRefreshConfig {
  id?: number;
  frequency: AIChatBotRefreshFrequency;
  scheduleTime: string;
  autoApply: boolean;
  aiChatbotId: number;
}

export type AIChatBotRefreshConfigCreate = Omit<AIChatBotRefreshConfig, 'id'>;
